import { useApolloClient } from '@apollo/client';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import type {
  ButtonProps,
  ButtonSizes,
  ButtonVariants,
} from '@xing-com/button';
import { Button } from '@xing-com/button';
import type {
  ApplyButtonPreScreeningLanguageFragment,
  ApplyButtonPreScreeningProfileModulesFragment,
} from '@xing-com/crate-jobs-graphql';
import { GetLanguagesAndWorkPermitDocument } from '@xing-com/crate-jobs-graphql';
import type { ApplicationData } from '@xing-com/crate-jobs-helpers';
import {
  useApplicationStatusHelpers,
  useApplicationTypeHelpers,
} from '@xing-com/crate-jobs-hooks';
import { useExperiment, useViewerData } from '@xing-com/hub';
import { IconApply, IconTick } from '@xing-com/icons';

import { useApplyButtonStateProps } from './apply-button-state-context';
import type { LanguageQuestionId } from './pre-screening-dialog';
import {
  PreScreeningDialog,
  useConfirmPreScreening,
} from './pre-screening-dialog';
import { useApplyButtonDefaultProps } from './use-apply-button-default-props';
import { useApplyButtonEmailApplyProps } from './use-apply-button-email-apply-props';
import { useApplyButtonMessageApplyProps } from './use-apply-button-message-apply-props';
import { useApplyButtonUrlApplyProps } from './use-apply-button-url-apply-props';
import type { ApplyTrackingProps } from './use-track-apply-intention';

type Props = {
  application: ApplicationData;
  size?: ButtonSizes;
  variant?: ButtonVariants;
  icon?: ButtonProps['icon'];
  iconOnly?: boolean;
  className?: string;
  trackingProps?: ApplyTrackingProps;
};

const Apply = styled(Button)`
  white-space: nowrap;
`;

const defaultLanguageQuestionIds: LanguageQuestionId[] = [
  'germanSkills',
  'englishSkills',
];

const getProfileLanguages = (
  profileModules?: ApplyButtonPreScreeningProfileModulesFragment | null
): ApplyButtonPreScreeningLanguageFragment[] =>
  profileModules?.languagesModule?.content?.collection?.filter(
    (language): language is ApplyButtonPreScreeningLanguageFragment =>
      language !== null
  ) ?? [];

const hasLanguageSkillLevel = (
  languages: ApplyButtonPreScreeningLanguageFragment[],
  identifier: string
): boolean =>
  Boolean(
    languages.some(
      (language) =>
        language.identifier === identifier &&
        language.skillLevelId !== null &&
        language.skillLevelId !== undefined
    )
  );

const getVisibleLanguageQuestionIds = (
  languages: ApplyButtonPreScreeningLanguageFragment[]
): LanguageQuestionId[] => {
  return defaultLanguageQuestionIds.filter((questionId) => {
    if (questionId === 'germanSkills') {
      return !hasLanguageSkillLevel(languages, 'de');
    }

    return !hasLanguageSkillLevel(languages, 'en');
  });
};

const hasEuWorkPermit = (
  profileModules?: ApplyButtonPreScreeningProfileModulesFragment | null
): boolean => {
  const euWorkPermit =
    profileModules?.workEligibilityModule?.content?.euWorkPermit;

  return euWorkPermit !== null && euWorkPermit !== undefined;
};

export const ApplyButton: React.FC<Props> = ({
  application,
  size = 'medium',
  variant = 'primary',
  icon,
  iconOnly,
  className,
  trackingProps,
}) => {
  const intl = useIntl();
  const preScreeningVariant = useExperiment('ABACUS-570') === 'B';
  const viewerId = useViewerData().data?.user?.id;
  const [showPreScreeningDialog, setShowPreScreeningDialog] = useState(false);
  const [visibleLanguageQuestionIds, setVisibleLanguageQuestionIds] = useState<
    LanguageQuestionId[]
  >(defaultLanguageQuestionIds);
  const [profileLanguages, setProfileLanguages] = useState<
    ApplyButtonPreScreeningLanguageFragment[]
  >([]);
  const [showWorkPermitQuestion, setShowWorkPermitQuestion] = useState(true);
  const { appliedAt } = useApplicationStatusHelpers(application);
  const apolloClient = useApolloClient();

  const {
    type,
    applyUrl,
    applyEmail,
    contactUserId,
    code,
    jobId,
    prioritized,
    slug,
    title,
  } = application;
  const hasApplied = !!appliedAt;

  const {
    isXingApplication,
    isCustomApplication,
    isEmailApplication,
    isMessageApplication,
    isUrlApplication,
  } = useApplicationTypeHelpers(type);

  const label = useMemo(() => {
    if (hasApplied) {
      return 'JOBS_MYJOBS_APPLIED_STATUS';
    }
    if (isXingApplication || isCustomApplication) {
      return 'JOBS_JOB_DETAILS_XING_APPLY';
    }
    if (isUrlApplication) {
      return 'JOB_POSTING_APPLY_EXTERNAL_URL';
    }
    return 'JOBS_JOB_DETAILS_APPLY';
  }, [hasApplied, isXingApplication, isCustomApplication, isUrlApplication]);

  const defaultProps = useApplyButtonDefaultProps(slug);
  const applyFlowProps = useApplyButtonStateProps();
  const messageApplyProps = useApplyButtonMessageApplyProps({
    contactUserId,
    jobId,
    trackingProps,
  });
  const urlApplyProps = useApplyButtonUrlApplyProps({
    jobId,
    slug,
    applyUrl,
    isPrioritizedJob: prioritized,
    trackingProps,
  });
  const emailApplyProps = useApplyButtonEmailApplyProps({
    code,
    applyEmail,
    title,
    jobId,
    trackingProps,
  });
  const additionalProps = {
    ...defaultProps,
    ...(isEmailApplication && emailApplyProps),
    ...(isMessageApplication && messageApplyProps),
    ...(isUrlApplication && urlApplyProps),
    ...((isXingApplication || isCustomApplication) && applyFlowProps),
  };

  const continueApplyFlow = (): void => {
    if (isXingApplication || isCustomApplication) {
      applyFlowProps.onClick(trackingProps);
    } else if (isUrlApplication) {
      urlApplyProps.onClick();
    } else {
      additionalProps.onClick?.();
    }
  };

  const shouldShowPreScreening = preScreeningVariant && isXingApplication;

  const openPreScreeningDialog = async (): Promise<void> => {
    if (!viewerId) {
      continueApplyFlow();
      return;
    }

    try {
      const { data } = await apolloClient.query({
        query: GetLanguagesAndWorkPermitDocument,
        variables: { id: viewerId },
        fetchPolicy: 'no-cache',
      });

      const languages = getProfileLanguages(data?.profileModules);
      const visibleLanguageQuestionIds =
        getVisibleLanguageQuestionIds(languages);
      const showWorkPermitQuestion = !hasEuWorkPermit(data?.profileModules);

      if (visibleLanguageQuestionIds.length === 0 && !showWorkPermitQuestion) {
        continueApplyFlow();
        return;
      }

      setProfileLanguages(languages);
      setVisibleLanguageQuestionIds(visibleLanguageQuestionIds);
      setShowWorkPermitQuestion(showWorkPermitQuestion);
    } catch {
      continueApplyFlow();
      return;
    }

    setShowPreScreeningDialog(true);
  };

  const handleOnClick = (event: React.MouseEvent): void => {
    event.stopPropagation();

    if (shouldShowPreScreening) {
      openPreScreeningDialog();
    } else {
      continueApplyFlow();
    }
  };

  const handlePreScreeningConfirm = useConfirmPreScreening({
    continueApplyFlow,
    profileLanguages,
    setShowPreScreeningDialog,
    showWorkPermitQuestion,
    viewerId,
    visibleLanguageQuestionIds,
  });

  const getIcon = (): ButtonProps['icon'] => {
    if (iconOnly) return IconApply;
    if (hasApplied) return IconTick;
    return icon ?? additionalProps.icon;
  };

  return (
    <>
      <Apply
        disabled={hasApplied}
        variant={variant}
        size={size}
        aria-label={intl.formatMessage({ id: label })}
        className={className}
        {...additionalProps}
        icon={getIcon()}
        onClick={handleOnClick}
      >
        {!iconOnly && <FormattedMessage id={label} />}
      </Apply>
      {shouldShowPreScreening && showPreScreeningDialog ? (
        <PreScreeningDialog
          show={showPreScreeningDialog}
          showWorkPermitQuestion={showWorkPermitQuestion}
          visibleLanguageQuestionIds={visibleLanguageQuestionIds}
          onCancel={() => setShowPreScreeningDialog(false)}
          onConfirm={handlePreScreeningConfirm}
        />
      ) : null}
    </>
  );
};
